"use client";

import Image from "next/image";

const NonSurgicalPeriodontalTreatment: React.FC = () => {
  return (
    <section className="w-full py-6 sm:py-8 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 space-y-6 text-center md:text-left">
        <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
          Periodontal treatment can be categorized in the following ways:
        </h3>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Non-Surgical Periodontal Treatment
          </h3>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            When periodontal disease is detected early in its onset,
            conservative or non-surgical methods of care combined with improved
            hygiene routines can restore periodontal health. While periodic,
            professional cleanings are sufficient to maintain periodontal health
            in patients that do not have gum disease, once gingivitis is
            present, deeper cleanings and possibly other non-surgical methods of
            care are recommended to treat the condition.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            The American Academy of Periodontology emphasizes achieving
            periodontal health by means of the least invasive and cost-effective
            treatment approaches to care. Deeper cleanings, which include
            Scaling and Root planing, are non-surgical procedures that are
            considered the first line of defense against the progression of
            periodontal disease.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            With scaling and root planing, any plaque and tartar (hardened
            dental plaque) that have accumulated below the gumline are carefully
            removed, and then the root surfaces of the teeth are smoothed. Since
            periodontal disease is an inflammatory response to plaque, tartar,
            and bacterial toxins, by simply mechanically eliminating these
            agents, the progression of gum disease can be halted. In addition to
            removing plaque and tartar with a scaling and root planing
            procedure, antimicrobial medication placed under the gumline or
            systemic medications can be used as adjuncts to care to further
            reduce the bacterial population.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            If non-surgical approaches to managing periodontal disease do not
            achieve the desired outcome, surgery may be recommended to halt its
            progression and repair damage as possible.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Periodontal Surgery
          </h3>

          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
              When gum disease has advanced beyond the initial stage,
              periodontal surgery is often recommended to effectively remove
              bacteria and tartar from around the teeth, reduce gingival pocket
              depth, restore lost tissue as possible, and halt disease
              progression. It’s essential to keep in mind that gum disease is a
              progressive condition. If appropriate measures are not taken, gum
              disease continues to compromise the support of the teeth and have
              consequences to one’s overall dental health and well-being.
            </p>
            <div className="w-full lg:w-[300px] xl:w-[360px] flex-shrink-0">
              <Image
                src="/images/system_images_services_dental_interior_periodontal_treatment_periodontal_treatment3.webp"
                alt="Periodontal Surgery"
                width={360}
                height={240}
                className="rounded-lg object-cover w-full h-auto"
              />
            </div>
          </div>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            As untreated gingivitis evolves into more advanced stages of gum
            disease, periodontal pockets deepen, the bacteria become more
            difficult to remove, and the gaps between the surface of the teeth
            and gums get larger. When pocket depth increases to the point of
            being beyond the reach of deep cleanings and other conservative
            methods of care (5mm or more), gum surgery to clean and treat the
            damage to gums and underlying bone is typically recommended.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            With scaling and root planing, any plaque and tartar (hardened
            dental plaque) that have accumulated below the gumline are carefully
            removed, and then the root surfaces of the teeth are smoothed. Since
            periodontal disease is an inflammatory response to plaque, tartar,
            and bacterial toxins, by simply mechanically eliminating these
            agents, the progression of gum disease can be halted. In addition to
            removing plaque and tartar with a scaling and root planing
            procedure, antimicrobial medication placed under the gumline or
            systemic medications can be used as adjuncts to care to further
            reduce the bacterial population.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            The good news is that with proper surgical treatment followed by
            improved oral hygiene, the chances of tooth loss, further damage to
            the bone and soft tissues supporting the teeth, and complications
            from health problems that are linked to periodontal disease can be
            decreased.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            The goals of pocket reduction surgery are to accomplish the
            following:
          </p>

          <ul className="list-disc list-outside space-y-4 pl-6 text-gray-700 text-base sm:text-lg leading-relaxed">
            <li>
              Remove sub-gingival bacteria beneath the gums and from the
              surfaces of the roots of the teeth
            </li>
            <li>
              Halt damage to the underlying bone, and recontour the affected
              hard tissues as needed
            </li>
            <li>
              Reduce pocket depth to facilitate easier cleaning of the teeth and
              gums and the maintenance of optimal periodontal health
            </li>
          </ul>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            In many cases, a periodontal procedure, which is known as flap
            surgery, is frequently recommended. During this procedure, the gums
            in the affected area are reflected away from the teeth and
            supporting bone. Now, the pocket and roots of the teeth can be
            thoroughly cleaned and supporting tissues recontoured as needed.
            When this step gets completed, the tissue flag gets sutured back
            into place.
          </p>

          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            Today, in addition to conventional surgical approaches to care,
            dental lasers are also used to perform select periodontal
            procedures. When applicable, laser technology can help treat
            periodontitis, reduce pocket depth, address peri-implantitis, and
            remove or recontour gingival tissues for cosmetic purposes.
          </p>
        </div>
      </div>
    </section>
  );
};

export default NonSurgicalPeriodontalTreatment;
